// packages/ui/src/components/Icon.tsx

import React from 'react';
import { icons, LucideProps } from 'lucide-react';

/**
 * Converts a kebab-case or mixed-case string to the strict PascalCase required by lucide-react.
 * e.g., 'building-2' -> 'Building2'
 * e.g., 'arrow-right' -> 'ArrowRight'
 * e.g., 'ArrowRight' -> 'ArrowRight' (no change)
 * @param str The string to convert.
 * @returns The PascalCase string.
 */
function toPascalCase(str: string): string {
  if (!str) return '';
  // This logic correctly handles kebab-case and preserves existing capitalization in words.
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Keep rest of word as-is
    .join('');
}

export type IconName = keyof typeof icons;

export interface IconProps extends LucideProps {
  name: string;
}

export const Icon: React.FC<IconProps> = ({ name, ...props }) => {
  // Convert the incoming name to PascalCase before lookup.
  const pascalCaseName = toPascalCase(name) as IconName;

  const LucideIcon = icons[pascalCaseName];

  if (!LucideIcon) {
    console.warn(`[Icon Component] Icon "${name}" (converted to "${pascalCaseName}") not found. Rendering fallback.`);
    // Using a known, safe fallback icon
    const FallbackIcon = icons['HelpCircle'];
    return <FallbackIcon {...props} />;
  }

  return <LucideIcon {...props} />;
};